import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { productsAPI, categoriesAPI, companiesAPI, productGroupsAPI } from '../../services/api';
import { ArrowLeft, Save, X } from 'lucide-react';
import toast from 'react-hot-toast';
import ImageUpload from '../../components/ImageUpload';

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedImage, setSelectedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [quantityValue, setQuantityValue] = useState('');
  const [quantityUnit, setQuantityUnit] = useState('ML');
  const isEditing = Boolean(id);

  // Available units for quantity
  const quantityUnits = [
    { value: 'ML', label: 'ML' },
    { value: 'L', label: 'L' },
    { value: 'Grams', label: 'Grams' },
    { value: 'KG', label: 'KG' }
  ];

  // Fetch product data if editing
  const { data: productData, isLoading: isLoadingProduct } = useQuery(
    ['product', id],
    () => productsAPI.getById(id),
    {
      enabled: isEditing,
      onError: (error) => {
        console.error('Failed to fetch product:', error);
        toast.error('Failed to load product data');
        navigate('/products');
      }
    }
  );

  const product = productData?.data?.data || productData?.data;

  // Fetch companies
  const { data: companiesData } = useQuery('companies', () => {
    return companiesAPI.getAll().then(response => response);
  }, {
    retry: 1,
    onError: (error) => {
      console.error('Companies fetch error:', error);
    }
  });

  // Fetch categories
  const { data: categoriesData } = useQuery('categories', () => {
    return categoriesAPI.getAll().then(response => response);
  }, {
    retry: 1,
    onError: (error) => {
      console.error('Categories fetch error:', error);
    }
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
      price: '',
      stock_quantity: 0,
      quantity: '', // This will store the combined value like "500ML"
      company_uuid: '',
      category_uuid: '',
      product_group_uuid: '',
    }
  });

  const watchedCompanyUuid = watch('company_uuid');
  const watchedCategoryUuid = watch('category_uuid');
  const watchedProductGroupUuid = watch('product_group_uuid');

  // Fetch product groups when category changes (only active ones)
  const { data: productGroupsData } = useQuery(
    ['product-groups', watchedCategoryUuid, 'active'],
    () => {
      if (watchedCategoryUuid) {
        return productGroupsAPI.getAll({ 
          category_uuid: watchedCategoryUuid,
          is_active: 'true'  // Only fetch active product groups
        });
      }
      return Promise.resolve({ data: [] });
    },
    { 
      enabled: !!watchedCategoryUuid,
      retry: 1,
      onError: (error) => {
        console.error('Product groups fetch error:', error);
      }
    }
  );

  // Set form values when product data is loaded
  useEffect(() => {
    if (product) {
      setValue('name', product.name || '');
      setValue('description', product.description || '');
      setValue('price', product.price || '');
      setValue('stock_quantity', product.stock_quantity || 0);
      setValue('company_uuid', product.company_uuid || '');
      setValue('category_uuid', product.category_uuid || '');
      setValue('product_group_uuid', product.product_group_uuid || '');
      
      // Parse existing quantity field
      if (product.quantity) {
        // Extract number and unit from strings like "500ML", "1L", "50 Grams", "17 KGs"
        const quantityMatch = product.quantity.match(/^(\d+(?:\.\d+)?)\s*(.+)$/);
        if (quantityMatch) {
          setQuantityValue(quantityMatch[1]);
          setQuantityUnit(quantityMatch[2]);
        }
      } else {
        setQuantityValue('');
        setQuantityUnit('ML');
      }
    }
  }, [product, setValue]);

  // Reset product group when category changes
  useEffect(() => {
    if (watchedCategoryUuid && watchedCategoryUuid !== product?.category_uuid) {
      setValue('product_group_uuid', '');
    }
  }, [watchedCategoryUuid, setValue, product?.category_uuid]);

  // Process data
  const companies = Array.isArray(companiesData?.data?.data?.companies) ? companiesData.data.data.companies : 
                   Array.isArray(companiesData?.data?.companies) ? companiesData.data.companies :
                   Array.isArray(companiesData?.data) ? companiesData.data : [];
  
  const categories = Array.isArray(categoriesData?.data?.data) ? categoriesData.data.data : 
                    Array.isArray(categoriesData?.data) ? categoriesData.data : [];
  
  const productGroups = Array.isArray(productGroupsData?.data?.data) ? productGroupsData.data.data : 
                       Array.isArray(productGroupsData?.data) ? productGroupsData.data : [];

  const mutation = useMutation(
    async (formData) => {
      if (isEditing) {
        return productsAPI.update(id, formData);
      } else {
        return productsAPI.create(formData);
      }
    },
    {
      onSuccess: () => {
        toast.success(isEditing ? 'Product updated successfully!' : 'Product created successfully!');
        queryClient.invalidateQueries('products');
        navigate('/products');
      },
      onError: (error) => {
        console.error('Mutation error:', error);
        toast.error(error.response?.data?.message || 'Operation failed');
      }
    }
  );

  const onSubmit = async (data) => {
    setIsUploading(true);
    try {
      // Validate image is selected for new products
      if (!selectedImage && !product?.image_url && !isEditing) {
        toast.error('Please select an image');
        setIsUploading(false);
        return;
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('description', data.description || '');
      formData.append('price', parseFloat(data.price));
      formData.append('stock_quantity', parseInt(data.stock_quantity));
      
      // Combine quantity value and unit
      if (quantityValue && quantityUnit) {
        const combinedQuantity = `${quantityValue}${quantityUnit}`;
        formData.append('quantity', combinedQuantity);
      }
      
      // Add company, category and product group
      formData.append('company_uuid', data.company_uuid);
      formData.append('category_uuid', data.category_uuid);
      if (data.product_group_uuid) {
        formData.append('product_group_uuid', data.product_group_uuid);
      }
      
      // Add mobile subscription eligibility
      formData.append('is_subscription_eligible', data.is_subscription_eligible ? '1' : '0');
      
      // Add image if selected
      if (selectedImage) {
        formData.append('image', selectedImage);
      }
      
      mutation.mutate(formData);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to save product');
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageChange = (file) => {
    setSelectedImage(file);
  };

  const handleCancel = () => {
    navigate('/products');
  };

  if (isLoadingProduct) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto p-3 sm:p-4 md:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4 mb-4">
            <button
              onClick={handleCancel}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
                {isEditing ? `Edit Product: ${product?.name}` : 'Create New Product'}
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                {isEditing ? 'Update product information' : 'Add a new product to your inventory'}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Product Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Product Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('name', { required: 'Product name is required' })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter product name"
              />
              {errors.name && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.name.message}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                {...register('description')}
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter product description"
              />
            </div>

            {/* Price and Stock */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Price (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  {...register('price', { 
                    required: 'Price is required',
                    min: { value: 0, message: 'Price must be positive' }
                  })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  placeholder="0.00"
                />
                {errors.price && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.price.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Stock Quantity <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  {...register('stock_quantity', { 
                    required: 'Stock quantity is required',
                    min: { value: 0, message: 'Stock quantity must be non-negative' }
                  })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  placeholder="0"
                />
                {errors.stock_quantity && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.stock_quantity.message}</p>
                )}
              </div>
            </div>

            {/* Quantity with Unit */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Quantity Value
                </label>
                <input
                  type="number"
                  value={quantityValue}
                  onChange={(e) => setQuantityValue(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  placeholder="Enter quantity value (e.g., 500, 1, 50)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Quantity Unit
                </label>
                <select
                  value={quantityUnit}
                  onChange={(e) => setQuantityUnit(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                >
                  {quantityUnits.map((unit) => (
                    <option key={unit.value} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Quantity Preview */}
            {quantityValue && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center text-sm text-blue-800 dark:text-blue-200">
                  <span className="font-medium">Preview:</span>
                  <span className="ml-2 bg-blue-100 dark:bg-blue-800 px-3 py-1 rounded-full text-blue-900 dark:text-blue-100 font-semibold">
                    {quantityValue}{quantityUnit}
                  </span>
                </div>
              </div>
            )}

            {/* New Hierarchy: Company → Category */}
            <div className="space-y-6">
              {/* Company Selection (Step 1) */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border-l-4 border-blue-500">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                  🏢 Step 1: Select Company
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(Required)</span>
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Company <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register('company_uuid', {
                      required: 'Company is required'
                    })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Select Company</option>
                    {companies.map((company) => (
                      <option key={company.uuid} value={company.uuid}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                  {errors.company_uuid && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.company_uuid.message}</p>
                  )}
                  {companies.length === 0 && (
                    <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                      No companies available. Please create a company first.
                    </p>
                  )}
                </div>
              </div>

              {/* Category Selection (Step 2) */}
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border-l-4 border-primary-500">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                  📁 Step 2: Select Category
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(Required)</span>
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...register('category_uuid', {
                      required: 'Category is required'
                    })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.uuid} value={category.uuid}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {errors.category_uuid && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.category_uuid.message}</p>
                  )}
                  {categories.length === 0 && (
                    <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                      No categories available. Please create a category first.
                    </p>
                  )}
                </div>
              </div>

              {/* Product Group Selection (Step 3) */}
              {watchedCategoryUuid && (
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border-l-4 border-purple-500">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                    📦 Step 3: Select Product Group
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      (Optional - from {categories.find(c => c.uuid === watchedCategoryUuid)?.name})
                    </span>
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Product Group (Optional)
                    </label>
                    <select
                      {...register('product_group_uuid')}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="">Select Product Group</option>
                      {productGroups.map((productGroup) => (
                        <option key={productGroup.uuid} value={productGroup.uuid}>
                          {productGroup.name}
                        </option>
                      ))}
                    </select>
                    {productGroups.length === 0 && (
                      <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                        No product groups available for this category. You can create one in Product Groups section.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Subscription Settings */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                📱 Mobile App Settings
                <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full">
                  Subscription Feature
                </span>
              </h3>
              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    {...register('is_subscription_eligible')}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Enable for Mobile Subscriptions
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    📱 Allow mobile app customers to include this product in their subscription plans.
                    <br />
                    💡 This will make the product available in subscription flows on the mobile app.
                  </p>
                </div>
              </div>
              <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
                <div className="flex items-start space-x-2">
                  <div className="text-yellow-600 dark:text-yellow-400 text-sm">⚠️</div>
                  <div className="text-xs text-yellow-800 dark:text-yellow-200">
                    <strong>Note:</strong> Products enabled for subscriptions will appear in mobile app subscription plans. 
                    Make sure pricing and stock levels are appropriate for recurring deliveries.
                  </div>
                </div>
              </div>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Product Image {!isEditing && <span className="text-red-500">*</span>}
              </label>
              <ImageUpload
                value={product?.image_url}
                onChange={handleImageChange}
                label="Product Image"
                required={!isEditing}
                error={!selectedImage && !product?.image_url && !isEditing ? 'Image is required' : ''}
              />
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUploading || mutation.isLoading}
                className="flex-1 sm:flex-none px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center gap-2"
              >
                {isUploading || mutation.isLoading ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {isEditing ? 'Update Product' : 'Create Product'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProductForm;